import { BRAND } from "constants/brand";
import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { Form } from "react-bootstrap";
import { Switch } from "@mui/material";
import { CSSTransition } from "react-transition-group";
import { Ring } from "@uiball/loaders";

import { useCityVendorWallet, useSaveCityVendorWallet } from "hooks/useVendorWallet";
import "../CityWallet/cityWallet.scss";

/**
 * محفظة المطاعم — one city's restaurant wallet policy (api#147, admin#41).
 *
 * THE SAME LAYOUT AS «محفظة السائقين» (CityWallet): the switch, the counts,
 * the warning before a switch goes on, then this side's rate. The restaurant
 * default rate lives here, beside the restaurant switch.
 *
 * Each restaurant also needs its own switch (the «المحفظة» column on the
 * restaurants table) before an order costs it anything. A restaurant on the
 * wallet with ₪0 or less closes the moment the city goes on, so the switch asks
 * for a deliberate confirmation.
 */
const CityVendorWallet = (props) => {
    const nodeRef = useRef();
    const cityId = props.show ? props.region?.id : null;
    const { policy, loading, error } = useCityVendorWallet(cityId);
    const save = useSaveCityVendorWallet();

    const [enabled, setEnabled] = useState(false);
    const [vendorPercentage, setVendorPercentage] = useState("");
    const [understood, setUnderstood] = useState(false);

    useEffect(() => {
        if (!policy) return;
        setEnabled(!!policy.vendor_wallet_enabled);
        setVendorPercentage(String(policy.vendor_commission_default_percentage ?? ""));
        setUnderstood(false);
    }, [policy]);

    const isTurningOn = enabled && policy?.vendor_wallet_enabled === false;
    const closingCount = policy?.emptyWalletCount ?? 0;
    const canSave = !save.isPending && Boolean(policy) && (!isTurningOn || understood);

    const submit = async (event) => {
        event.preventDefault();

        try {
            const result = await save.mutateAsync({
                cityId: props.region.id,
                enabled,
                vendorPercentage: Number(vendorPercentage),
            });
            props.showMessage(
                "success",
                "تم الحفظ",
                result.vendor_wallet_enabled
                    ? `محفظة المطاعم مفعلة. ${result.blockedCount} مطعم مغلق بسبب عدم كفاية الرصيد.`
                    : "محفظة المطاعم غير مفعلة في هذه المدينة. رُفع إغلاق المحفظة، ولم يُفتح أي مطعم تلقائياً.",
            );
            props.close();
        } catch (err) {
            props.showMessage(
                "error",
                "تعذر الحفظ",
                err.response?.data?.message || "حدث خطأ غير متوقع",
            );
        }
    };

    return (
        <div className={`model ${props.show ? "model-show" : ""}`} onClick={props.close}>
            <CSSTransition
                mountOnEnter
                unmountOnExit
                nodeRef={nodeRef}
                in={props.show}
                timeout={400}
                classNames="show"
            >
                <div
                    className="addItem city-wallet"
                    ref={nodeRef}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="head center">
                        <h5>محفظة المطاعم — {props.region?.name}</h5>
                        <FontAwesomeIcon icon={faClose} onClick={props.close} />
                    </div>

                    {loading ? (
                        <div className="loading-spinner center">
                            <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                        </div>
                    ) : error ? (
                        <p className="city-wallet__counts">{error}</p>
                    ) : (
                        <Form className="form" onSubmit={submit}>
                            <div className="city-wallet__switch surface-card">
                                <div>
                                    <strong>تفعيل محفظة المطاعم في هذه المدينة</strong>
                                    <p className="city-wallet__switch-hint">
                                        عند التفعيل، تُخصم عمولة كل طلب من رصيد المطعم عند إنشائه.
                                        يُطبق فقط على المطاعم المفعّل لها نظام المحفظة.
                                    </p>
                                </div>
                                <Switch
                                    checked={enabled}
                                    onChange={(e) => setEnabled(e.target.checked)}
                                />
                            </div>

                            <p className="city-wallet__counts">
                                {policy?.walletVendorCount ?? 0} من {policy?.vendorCount ?? 0} مطعم
                                على نظام المحفظة، منهم {closingCount} رصيده صفر أو أقل
                                {policy?.vendor_wallet_enabled
                                    ? ` و${policy?.blockedCount ?? 0} مغلق الآن بسبب الرصيد.`
                                    : "."}
                            </p>

                            {/* The consequence, with the real number, BEFORE the
                                save. A closed restaurant simply stops getting
                                orders — nothing else would say why. */}
                            {isTurningOn ? (
                                <div className="city-wallet__warning">
                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                    <div>
                                        <strong>
                                            {closingCount} من {policy?.walletVendorCount ?? 0} مطعم
                                            على المحفظة رصيده صفر أو أقل.
                                        </strong>
                                        <p>
                                            بعد التفعيل سيُغلق كل مطعم رصيده صفر أو أقل، ولن يستقبل
                                            طلبات جديدة حتى يُشحن رصيده بأكثر من صفر. اشحن المحافظ
                                            أولاً، ثم فعّل.
                                        </p>
                                        <Form.Check
                                            type="checkbox"
                                            id="city-vendor-wallet-understood"
                                            checked={understood}
                                            onChange={(e) => setUnderstood(e.target.checked)}
                                            label="أفهم أن التفعيل يغلق المطاعم التي رصيدها صفر أو أقل"
                                        />
                                    </div>
                                </div>
                            ) : null}

                            <div className="row row-cols-1 row-cols-md-2 g-2">
                                <div className="col">
                                    <Form.Label>نسبة المطعم الافتراضية (%)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        value={vendorPercentage}
                                        onChange={(e) => setVendorPercentage(e.target.value)}
                                    />
                                    <p className="city-wallet__hint">
                                        تُستخدم للمطعم الذي ليس له نسبة خاصة به، في المحفظة وفي كشف
                                        الحساب.
                                    </p>
                                </div>
                            </div>

                            <div className="buttons">
                                <button className="button" type="submit" disabled={!canSave}>
                                    {save.isPending ? "جاري الحفظ..." : "حفظ الإعدادات"}
                                </button>
                            </div>
                        </Form>
                    )}
                </div>
            </CSSTransition>
        </div>
    );
};

export default CityVendorWallet;
